import { Alert, Button, Stack, Typography } from '@mui/material';
import { TextButton } from 'design-system';
import { ConfirmationDialog } from '@src/design-system/dialogs/ConfirmationDialog';
import { FC } from 'react';

interface PropTypes {
  open: boolean;
  onClose: () => void;
  numberOfDepartures: number;
  targetItineraryDescription: string;
  /** Set when the target itinerary belongs to a different adventure */
  targetAdventureLabel?: string;
  deselectAll: () => void;
  handleMoveSelectedDepartures: (copyTargetTimeline: boolean) => Promise<void>;
}

export const MoveDeparturesDialog: FC<PropTypes> = ({
  open,
  onClose,
  numberOfDepartures,
  targetItineraryDescription,
  targetAdventureLabel,
  deselectAll,
  handleMoveSelectedDepartures,
  ...props
}) => {
  const title = `Update ${numberOfDepartures} departure${
    numberOfDepartures > 1 ? 's' : ''
  } with new itinerary?`;

  const dialogBodyText = `By selecting "Lose Changes" departures will inherit the itinerary from ${targetItineraryDescription}`;

  const updateItinerary = (copyTargetTimeline: boolean) => {
    void handleMoveSelectedDepartures(copyTargetTimeline);
    onClose();
  };

  return (
    <ConfirmationDialog
      {...props}
      open={open}
      onCancel={onClose}
      title={title}
      hasCustomActions={true}
      maxWidth={'sm'}
    >
      <Typography variant="bodyPara">{dialogBodyText}</Typography>
      <Alert severity="warning" data-testid="update-itinerary-warning">
        <Stack spacing={1.5}>
          <div>
            <strong>Lose Changes:</strong> Will move the departures and apply the master
            itinerary from the tour you are moving them to. Individual departure level
            changes will be lost
          </div>
          <div>
            <strong>Keep Changes:</strong> Will move the departures, apply the master
            itinerary from the tour you are moving them to and retain individual departure
            level changes. <strong>IMPORTANT:</strong> Differences between the destination
            itinerary and the source itinerary will display as individual changes.
          </div>
        </Stack>
      </Alert>
      {targetAdventureLabel && (
        <Alert severity="warning" data-testid="move-departures-cross-adventure-warning">
          These departures will move to <strong>{targetAdventureLabel}</strong>. Ensure
          the Finance team are informed so they can update Booking Engine as necessary
        </Alert>
      )}
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <TextButton onClick={() => onClose()}>Cancel</TextButton>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button onClick={() => updateItinerary(false)} variant="outlined">
            Keep Changes
          </Button>
          <Button onClick={() => updateItinerary(true)} variant="contained">
            Lose Changes
          </Button>
        </Stack>
      </Stack>
    </ConfirmationDialog>
  );
};
